import { motion, useInView } from 'framer-motion';
import { Utensils, Leaf, Flame, ChefHat } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useRef } from 'react';

const icons = [Utensils, Leaf, Flame, ChefHat];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { 
    opacity: 0, 
    y: 50,
    scale: 0.8
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12
    }
  }
};

const iconVariants = {
  hidden: { scale: 0, rotate: -180 },
  visible: {
    scale: 1,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 200,
      delay: 0.2
    }
  },
  hover: {
    scale: 1.2,
    rotate: 360,
    transition: { duration: 0.5 }
  }
};

export function WhyChooseUs() {
  const { t, isRTL } = useLanguage();
  const items = t('whyUs.items');
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section 
      className="py-24 sm:py-32 bg-[#FDF8F4] overflow-hidden"
      data-testid="why-choose-us-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 100 }}
            className={`font-cormorant text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#6B1C23] ${
              isRTL ? 'font-noto-serif-arabic' : ''
            }`}
            data-testid="why-us-title"
          >
            {t('whyUs.title')}
          </motion.h2>
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="h-1 w-24 bg-[#6B1C23] mx-auto mt-4"
          />
        </motion.div>

        {/* Features Grid with Stagger */}
        <motion.div 
          ref={ref}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {items.map((item, index) => {
            const Icon = icons[index];
            return (
              <motion.div
                key={item.id}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="text-center group"
                data-testid={`why-us-item-${index}`}
              >
                <motion.div 
                  className="inline-flex items-center justify-center w-20 h-20 mb-6 bg-[#6B1C23]/10 group-hover:bg-[#6B1C23] transition-colors duration-500 rounded-full relative"
                  variants={iconVariants}
                  whileHover="hover"
                >
                  <Icon className="w-10 h-10 text-[#6B1C23] group-hover:text-white transition-colors duration-500" />
                  {/* Pulse Ring */}
                  <motion.div
                    className="absolute inset-0 rounded-full border-2 border-[#6B1C23]/30"
                    animate={{
                      scale: [1, 1.3, 1],
                      opacity: [0.5, 0, 0.5]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: index * 0.3
                    }}
                  />
                </motion.div>
                <motion.h3 
                  className={`font-cormorant text-xl font-semibold text-[#6B1C23] mb-3 ${
                    isRTL ? 'font-noto-serif-arabic' : ''
                  }`}
                  whileHover={{ scale: 1.05 }}
                >
                  {item.title}
                </motion.h3>
                <p className={`font-manrope text-[#6B6B6B] text-sm ${isRTL ? 'font-cairo' : ''}`}>
                  {item.description}
                </p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
